// src/components/tutorials/JS_String_Search.js
import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

// 🔹 Helper: Random border color generator
function getRandomColor() {
  const colors = [
    "#2563eb", // blue
    "#16a34a", // green
    "#dc2626", // red
    "#d97706", // orange
    "#9333ea", // purple
    "#0891b2", // teal
    "#f59e0b", // amber
    "#e11d48", // rose
    "#0ea5e9", // sky
    "#84cc16", // lime
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

// 🔹 Output Box Component (random color on each render)
function OutputBox({ children }) {
  const borderColor = getRandomColor();
  return (
    <div
      className="output-box"
      style={{
        border: `2px dashed ${borderColor}`,
        transition: "border-color 0.3s ease",
      }}
    >
      {children}
    </div>
  );
}

export default function JS_String_Search() {
  // 🔹 Refs for sections
  const refs = {
    indexOf: useRef(null),
    lastIndexOf: useRef(null),
    search: useRef(null),
    match: useRef(null),
    matchAll: useRef(null),
    includes: useRef(null),
    startsWith: useRef(null),
    endsWith: useRef(null),
  };

  const scrollTo = (key) => {
    const r = refs[key];
    if (r && r.current) {
      r.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // 🔹 Key point links
  const keyPoints = [
    { key: "indexOf", label: "String indexOf()" },
    { key: "lastIndexOf", label: "String lastIndexOf()" },
    { key: "search", label: "String search()" },
    { key: "match", label: "String match()" },
    { key: "matchAll", label: "String matchAll()" },
    { key: "includes", label: "String includes()" },
    { key: "startsWith", label: "String startsWith()" },
    { key: "endsWith", label: "String endsWith()" },
  ];

  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">String Search Methods</h1>

      {/* Introduction */}
      <p className="tutorial-intro">
        JavaScript provides several powerful methods for{" "}
        <strong>searching, matching, and finding</strong> text inside strings.
        These methods can return indexes, booleans, or arrays depending on their
        usage.
      </p>

      {/* Key Points Section */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
        }}
      >
        <h1 style={{ color: "#92400e", fontSize: "2.4rem", margin: 0 }}>
          String Search Key Methods
        </h1>
        <p style={{ fontSize: "1.05rem", marginTop: "0.5rem" }}>
          Click any topic below to explore examples and try them interactively!
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            marginTop: "1.25rem",
          }}
        >
          {keyPoints.map((l) => (
            <p key={l.key} style={{ margin: "0.25rem 0" }}>
              <button
                onClick={() => scrollTo(l.key)}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  margin: 0,
                  textDecoration: "underline",
                  color: "#000",
                  cursor: "pointer",
                  fontSize: "1.05rem",
                }}
              >
                {l.label}
              </button>
            </p>
          ))}
        </div>
      </div>

      {/* 1️⃣ indexOf */}
      <div ref={refs.indexOf} className="tutorial-section">
        <h2 className="section-title">1️⃣ String indexOf()</h2>
        <p>
          <b>What it does:</b> Returns the first index where the specified value
          is found, or <code>-1</code> if not found. It’s case-sensitive.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let text = "Hello world, welcome to JavaScript!";
console.log(text.indexOf("world")); // 6
console.log(text.indexOf("JavaScript")); // 25
console.log(text.indexOf("python")); // -1`}
          />
          <TryItButton
            code={`let text = "Hello world, welcome to JavaScript!";
console.log(text.indexOf("world"));
console.log(text.indexOf("JavaScript"));
console.log(text.indexOf("python"));`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>6 25 -1</pre>
        </OutputBox>
      </div>

      {/* 2️⃣ lastIndexOf */}
      <div ref={refs.lastIndexOf} className="tutorial-section">
        <h2 className="section-title">2️⃣ String lastIndexOf()</h2>
        <p>
          <b>What it does:</b> Returns the last index where the specified value
          is found. Searches backward from the end of the string.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let text = "The rain in Spain stays mainly in the plain";
console.log(text.lastIndexOf("in")); // 33
console.log(text.lastIndexOf("Spain")); // 12`}
          />
          <TryItButton
            code={`let text = "The rain in Spain stays mainly in the plain";
console.log(text.lastIndexOf("in"));
console.log(text.lastIndexOf("Spain"));`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>33 12</pre>
        </OutputBox>
      </div>

      {/* 3️⃣ search */}
      <div ref={refs.search} className="tutorial-section">
        <h2 className="section-title">3️⃣ String search()</h2>
        <p>
          <b>What it does:</b> Searches the string for a pattern (using a
          regular expression) and returns the index of the first match or{" "}
          <code>-1</code> if not found.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let text = "I love JavaScript!";
console.log(text.search(/java/i)); // 7 (case-insensitive with /i)
console.log(text.search("Python")); // -1`}
          />
          <TryItButton
            code={`let text = "I love JavaScript!";
console.log(text.search(/java/i));
console.log(text.search("Python"));`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>7 -1</pre>
        </OutputBox>
      </div>

      {/* 4️⃣ match */}
      <div ref={refs.match} className="tutorial-section">
        <h2 className="section-title">4️⃣ String match()</h2>
        <p>
          <b>What it does:</b> Returns an array containing the matched results
          when using a regular expression. If no match, returns{" "}
          <code>null</code>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let text = "The rain in SPAIN stays mainly in the plain";
console.log(text.match(/ain/g)); // ["ain","ain","ain"]
console.log(text.match(/ain/i)); // ["ain"]`}
          />
          <TryItButton
            code={`let text = "The rain in SPAIN stays mainly in the plain";
console.log(text.match(/ain/g));
console.log(text.match(/ain/i));`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>["ain","ain","ain"] ["ain"]</pre>
        </OutputBox>
      </div>

      {/* 5️⃣ matchAll */}
      <div ref={refs.matchAll} className="tutorial-section">
        <h2 className="section-title">5️⃣ String matchAll()</h2>
        <p>
          <b>What it does:</b> Returns an iterator for all RegExp matches. You
          can convert it into an array with the spread operator.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let text = "I love cats. Cats are cute.";
let regex = /cats/gi;
let matches = [...text.matchAll(regex)];
console.log(matches.map(m => m[0])); // ["cats", "Cats"]`}
          />
          <TryItButton
            code={`let text = "I love cats. Cats are cute.";
let regex = /cats/gi;
let matches = [...text.matchAll(regex)];
console.log(matches.map(m => m[0]));`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>["cats","Cats"]</pre>
        </OutputBox>
      </div>

      {/* 6️⃣ includes */}
      <div ref={refs.includes} className="tutorial-section">
        <h2 className="section-title">6️⃣ String includes()</h2>
        <p>
          <b>What it does:</b> Returns <code>true</code> if the string contains
          the specified substring. Case-sensitive.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let text = "Hello world!";
console.log(text.includes("world")); // true
console.log(text.includes("World")); // false`}
          />
          <TryItButton
            code={`let text = "Hello world!";
console.log(text.includes("world"));
console.log(text.includes("World"));`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>true false</pre>
        </OutputBox>
      </div>

      {/* 7️⃣ startsWith */}
      <div ref={refs.startsWith} className="tutorial-section">
        <h2 className="section-title">7️⃣ String startsWith()</h2>
        <p>
          <b>What it does:</b> Checks if a string begins with a specific
          substring. Optionally, you can specify a position to start checking
          from.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let text = "JavaScript is fun";
console.log(text.startsWith("Java")); // true
console.log(text.startsWith("Script")); // false
console.log(text.startsWith("Script", 4)); // true`}
          />
          <TryItButton
            code={`let text = "JavaScript is fun";
console.log(text.startsWith("Java"));
console.log(text.startsWith("Script"));
console.log(text.startsWith("Script", 4));`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>true false true</pre>
        </OutputBox>
      </div>

      {/* 8️⃣ endsWith */}
      <div ref={refs.endsWith} className="tutorial-section">
        <h2 className="section-title">8️⃣ String endsWith()</h2>
        <p>
          <b>What it does:</b> Checks if a string ends with a specific
          substring. Optionally, you can specify a length to consider as the
          string end.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let text = "Hello world";
console.log(text.endsWith("world")); // true
console.log(text.endsWith("Hello")); // false
console.log(text.endsWith("Hello", 5)); // true`}
          />
          <TryItButton
            code={`let text = "Hello world";
console.log(text.endsWith("world"));
console.log(text.endsWith("Hello"));
console.log(text.endsWith("Hello", 5));`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>true false true</pre>
        </OutputBox>
      </div>

      {/* Footer / Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Combine search methods with <code>replace()</code> or{" "}
            <code>matchAll()</code> for complex text processing.
          </li>
          <li>
            Learn Regular Expressions deeply to master <code>match()</code> and{" "}
            <code>search()</code>.
          </li>
          <li>
            Try chaining checks like{" "}
            <code>if (str.startsWith("Hi") && str.includes("user"))</code>.
          </li>
        </ul>
      </div>
    </div>
  );
}
